import { PaymentElement, useCheckout } from '@stripe/react-stripe-js';
import { SubmitEvent, useState } from 'react';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { CheckoutErrorModal } from './CheckoutErrorModal';
import { STRIPE_LINK, UNEXPECTED_ERROR_MESSAGE } from '@/const/shared';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import StripeIcon from '@/images/StripeIcon.svg?react';
import { StripeLinkWrapper } from '@/bundle/shared/components/Checkout/styles';
import { StripeCheckoutSession } from '@stripe/stripe-js';
import { useMutation } from '@tanstack/react-query';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { confirmSinglePayment, confirmSubscription } from '../api';

type CheckoutFormType = {
  onSuccess: () => void;
  loading: boolean;
  onLoadingEnd: () => void;
  paymentType: 'subscription' | 'singlePayment';
};

export const CheckoutForm = ({ paymentType = 'subscription', onSuccess, loading, onLoadingEnd }: CheckoutFormType) => {
  const checkout = useCheckout();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const { mutate: confirmPaymentMutate, isPending: isPendingConfirmPayment } = useMutation({
    mutationKey: ['confirm_payment'],
    mutationFn: (checkoutSession: StripeCheckoutSession) => {
      const payload = {
        session_id: checkoutSession.id,
      };

      const confirmPaymentFunction = paymentType === 'subscription' ? confirmSubscription : confirmSinglePayment;

      return confirmPaymentFunction(payload);
    },
    onSuccess(response) {
      if (response?.error) {
        const error = getResponseError(response?.error);

        setErrorMessage(error);
        setOpenErrorModal(true);

        return;
      }

      onSuccess();
    },
  });

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    setErrorMessage(null);
    setIsLoadingRequest(true);

    try {
      const confirmResponse = await checkout.confirm({ redirect: 'if_required' });

      // Check for local card info validations
      if (!checkout.canConfirm) return;

      if (confirmResponse.type === 'error') {
        setErrorMessage(confirmResponse.error.message);
        setOpenErrorModal(true);

        return;
      }

      confirmPaymentMutate(confirmResponse?.session);
    } catch (error) {
      console.error('Stripe checkout failed with', error?.message);

      setErrorMessage(UNEXPECTED_ERROR_MESSAGE);
      setOpenErrorModal(true);
    } finally {
      setIsLoadingRequest(false);
    }
  };

  const closeSubscriptionErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessage(null);
  };

  const disabledState = isLoadingRequest || isPendingConfirmPayment;

  return (
    <Box height='100%'>
      <form onSubmit={handleSubmit}>
        <PaymentElement
          id='payment-element'
          options={{
            layout: {
              type: 'accordion',
              defaultCollapsed: false,
            },
            readOnly: disabledState,
            paymentMethodOrder: ['card'],
            fields: {
              billingDetails: {
                email: 'never',
              },
            },
          }}
          onLoadError={onLoadingEnd}
          onReady={onLoadingEnd}
        />
        {!loading && (
          <Box marginTop='24px'>
            <Button disabled={disabledState} isLoading={disabledState} type='submit'>
              {`Pay ${checkout.total.total.amount} now`}
            </Button>
          </Box>
        )}
      </form>
      {!loading && (
        <StripeLinkWrapper href={STRIPE_LINK} target='_blank'>
          <Typography variant='captionSmall' color={COLORS.grey[600]} fontWeight='medium'>
            Powered by{' '}
          </Typography>
          <StripeIcon />
        </StripeLinkWrapper>
      )}
      <CheckoutErrorModal isOpen={openErrorModal} errorMessage={errorMessage} onClose={closeSubscriptionErrorModal} />
    </Box>
  );
};
